/**
 * useAuth — checks whether the current user has a valid qryptauth session.
 *
 * Calls GET /auth/me on mount.  Returns loading=true while the request is in
 * flight; sets authenticated=true when the server responds 200 with an accountId.
 * A 401 or network error produces authenticated=false without throwing.
 */
import { useState, useEffect } from 'react'; // rationale: standard React hooks for state and side effects

export interface AuthState {
    accountId: string | null; // populated when authenticated
    loading: boolean; // true while /auth/me is in flight
    authenticated: boolean; // true once a valid session is confirmed
}

/**
 * Hook that resolves the current session state by probing /auth/me.
 * @returns {AuthState} current authentication state
 */
export function useAuth(): AuthState {
    console.log('useAuth is running');
    const [state, setState] = useState<AuthState>({ accountId: null, loading: true, authenticated: false });

    useEffect(() => {
        console.log('useAuth effect is running');
        let cancelled = false; // guard against state updates after unmount

        fetch('/auth/me', { credentials: 'include' }) // include sid cookie in cross-context requests
            .then(res => {
                if (!res.ok) return null; // 401 or other error means unauthenticated
                return res.json() as Promise<{ accountId?: string }>;
            })
            .then(data => {
                if (cancelled) return;
                const accountId = data?.accountId ?? null;
                setState({ accountId, loading: false, authenticated: !!accountId });
                console.log(`useAuth effect is returning accountId=${accountId}`);
            })
            .catch(() => {
                if (cancelled) return;
                setState({ accountId: null, loading: false, authenticated: false }); // network failure → treat as unauthenticated
            });

        return () => { cancelled = true; }; // cleanup prevents stale state on unmount
    }, []);

    console.log(`useAuth is returning loading=${state.loading} authenticated=${state.authenticated}`);
    return state;
}
